import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Button, Modal, Form, Table, Badge } from 'react-bootstrap';
import API from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useApprovals } from './ApprovalProvider';
import { formatDateTime } from '../utils/dates';

const when = (d) => formatDateTime(d);
const VIA = { email: 'por correo', app: 'en la aplicación', password: 'con contraseña' };

const StatusBadge = ({ status }) => (
  <Badge bg={status === 'approved' ? 'success' : status === 'denied' ? 'danger' : 'secondary'}>
    {status === 'approved' ? 'Aprobada' : status === 'denied' ? 'Denegada' : status}
  </Badge>
);

// Denying asks for an optional reason, which the person who asked sees.
const DenyModal = ({ request, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await onDone(request, 'deny', reason.trim()); onClose(); } finally { setBusy(false); }
  };
  return (
    <Modal show onHide={busy ? undefined : onClose} centered>
      <Modal.Header closeButton={!busy}><Modal.Title>Denegar solicitud</Modal.Title></Modal.Header>
      <Modal.Body>
        <div className="mb-3"><strong>{request.label}</strong> — {request.target.label}<div className="small text-muted">Pedido por {request.requestedBy}</div></div>
        <Form.Label className="d-block small text-muted mb-1">Motivo <span>(opcional; {request.requestedBy} lo verá en Aprobaciones)</span></Form.Label>
        <Form.Control as="textarea" rows={3} maxLength={300} autoFocus value={reason} onChange={e => setReason(e.target.value)} placeholder="Ej.: este cliente todavía tiene facturas abiertas." />
        <div className="small text-muted text-end">{reason.length}/300</div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" disabled={busy} onClick={onClose}>Volver</Button>
        <Button variant="danger" disabled={busy} onClick={submit}>{busy ? 'Denegando...' : 'Confirmar denegación'}</Button>
      </Modal.Footer>
    </Modal>
  );
};

// Aprobaciones: what sales users asked of the admins. A sales user asks and moves on — an admin answers here (or from the
// email's links) and the action is carried out by that answer. Admins see every request and can decide; sales users see their own
// and what became of them.
const ApprovalsPage = () => {
  const { user } = useAuth();
  const { refresh } = useApprovals();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null); // { tone, text }
  const [busyId, setBusyId] = useState('');
  const [denying, setDenying] = useState(null);
  // ?solicitud=<id>: the WhatsApp alert opens the page on its request (marked and scrolled to).
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('solicitud');
  useEffect(() => {
    if (focusId && data) document.getElementById(`solicitud-${focusId}`)?.scrollIntoView({ block: 'center' });
  }, [focusId, data]);

  const load = useCallback(() => API.get('/approvals')
    .then(r => { setData(r.data); setError(''); })
    .catch(() => setError('No se pudo cargar la lista de solicitudes.')), []);
  useEffect(() => { load(); }, [load]);

  const decide = async (request, decision, reason) => {
    setBusyId(request.id); setNotice(null);
    try {
      const { data: result } = await API.post(`/approvals/${request.id}/decide`, { decision, ...(reason ? { reason } : {}) });
      setNotice({ tone: decision === 'approve' ? 'success' : 'secondary', text: decision === 'approve' ? (result.resultMessage || 'Aprobada.') : `Denegaste: ${request.label} ${request.target.label}.` });
    } catch (err) {
      const message = err.response?.data?.message || 'No se pudo registrar tu decisión.';
      // 409: already decided elsewhere (nothing to do), or it could not be carried out and stays open — say why either way.
      setNotice({ tone: err.response?.status === 409 ? 'warning' : 'danger', text: message });
    } finally {
      setBusyId(''); load(); refresh();
    }
  };

  return (
    <div className="container-fluid p-4">
      <h2 className="app-title mb-2" style={{ textAlign: 'left' }}>Aprobaciones</h2>
      <div className="small text-muted mb-4">
        {isAdmin
          ? 'Lo que los usuarios de ventas pidieron y esperan que un administrador complete. Al aprobar, la acción se realiza en ese momento; al denegar, no se hace nada y quien la pidió ve tu motivo. También puedes responder desde el correo.'
          : 'Las acciones que necesitan la aprobación de un administrador (anular, eliminar, notas de crédito y débito...) se piden y tú sigues con tu trabajo: aquí ves en qué quedó cada una.'}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}
      {notice && <div className={`alert ${{ success: 'alert-success', danger: 'alert-danger', warning: 'alert-warn', secondary: 'alert-note' }[notice.tone]}`} role="status">{notice.text}</div>}

      <h5 className="mb-3">{isAdmin ? 'Por decidir' : 'Esperando a un administrador'}{data ? ` (${data.pending.length})` : ''}</h5>
      {data && data.pending.length === 0 && <Card className="p-3 mb-4 text-muted dashboard-card">No hay solicitudes pendientes.</Card>}
      {data?.pending.map(r => (
        <Card key={r.id} id={`solicitud-${r.id}`} className="p-3 mb-2 dashboard-card" style={r.id === focusId ? { borderColor: 'var(--mm-cyan)', boxShadow: '0 0 0 2px var(--mm-cyan)' } : undefined}>
          <div className="d-flex flex-wrap justify-content-between gap-3">
            <div style={{ minWidth: 0, flex: '1 1 320px' }}>
              <div className="small text-muted text-uppercase" style={{ letterSpacing: '0.05em' }}>{r.label}</div>
              <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{r.target.label}{r.target.detail && <span className="text-muted" style={{ fontWeight: 400 }}> — {r.target.detail}</span>}</div>
              {r.summary && <div className="mt-1" style={{ overflowWrap: 'anywhere' }}>{r.summary}</div>}
              <div className="small text-muted mt-1">
                Pedido por <strong>{r.requestedBy}</strong> · {when(r.createdAt)}
                {isAdmin && (r.emailed ? ` · se envió correo a ${r.emailed} administrador${r.emailed === 1 ? '' : 'es'}` : ' · no se pudo avisar por correo')}
              </div>
              {r.failure && <div className="alert alert-warn py-2 mt-2 mb-0 small">La última vez no se pudo completar: {r.failure}</div>}
            </div>
            {(isAdmin || r.entityType === 'invoices') && (
              <div className="d-flex gap-2 align-items-start">
                {r.entityType === 'invoices' && (
                  <Button variant="outline-primary" onClick={() => window.open(`/invoices/${r.entityId}/preview`, '_blank')}>
                    <i className="bi bi-eye" /> Ver factura
                  </Button>
                )}
                {isAdmin && <>
                <Button variant="success" disabled={!!busyId} onClick={() => decide(r, 'approve')}>{busyId === r.id ? 'Aprobando...' : 'Aprobar'}</Button>
                <Button variant="outline-danger" disabled={!!busyId} onClick={() => setDenying(r)}>Denegar</Button>
                </>}
              </div>
            )}
          </div>
        </Card>
      ))}

      <h5 className="mt-4 mb-3">{isAdmin ? 'Resueltas en los últimos 14 días' : 'Mis solicitudes resueltas (14 días)'}</h5>
      <Card className="dashboard-card">
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle">
            <thead>
              <tr><th>Resuelta</th><th>Acción</th><th>Sobre</th><th>Pedido por</th><th>Decidió</th><th>Estado</th><th>Detalle</th></tr>
            </thead>
            <tbody>
              {data && data.recent.length === 0 && <tr><td colSpan={7} className="text-muted text-center py-3">Nada resuelto todavía.</td></tr>}
              {data?.recent.map(r => (
                <tr key={r.id}>
                  <td className="text-nowrap">{r.decidedAt ? when(r.decidedAt) : '—'}</td>
                  <td>{r.label}</td>
                  <td style={{ overflowWrap: 'anywhere' }}>{r.target.label}</td>
                  <td>{r.requestedBy}</td>
                  <td>{r.decidedBy || '—'}{r.decidedVia && <span className="text-muted small"> ({VIA[r.decidedVia]})</span>}</td>
                  <td><StatusBadge status={r.status} /></td>
                  <td style={{ overflowWrap: 'anywhere', maxWidth: 380 }}>
                    {r.status === 'denied' ? (r.reason ? `Motivo: ${r.reason}` : 'Sin motivo') : (r.resultMessage || '')}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Card>

      {denying && <DenyModal request={denying} onClose={() => setDenying(null)} onDone={decide} />}
    </div>
  );
};

export default ApprovalsPage;
